import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { FileText, Plus, Download, Trash2, Loader2, File, Shield, ExternalLink, UploadCloud, X, FileSpreadsheet, Image as ImageIcon, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../../components/ui/confirm-delete-dialog';
import { uploadToCloudinary, isCloudinaryConfigured } from '../../lib/cloudinary';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// Extension/format d'un nom de fichier ou d'une URL.
const extOf = (s = '') => {
  const clean = s.split('?')[0].split('#')[0];
  const m = clean.match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toLowerCase() : '';
};

// Méta d'affichage selon le format (icône, couleur, libellé).
const fileMeta = (fmt = '') => {
  const f = (fmt || '').toLowerCase();
  if (f === 'pdf') return { icon: FileText, color: 'text-red-600', bg: 'bg-red-500/10', label: 'PDF' };
  if (['doc', 'docx', 'odt', 'rtf'].includes(f)) return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-500/10', label: f.toUpperCase() };
  if (['xls', 'xlsx', 'csv', 'ods'].includes(f)) return { icon: FileSpreadsheet, color: 'text-emerald-600', bg: 'bg-emerald-500/10', label: f.toUpperCase() };
  if (['ppt', 'pptx', 'odp'].includes(f)) return { icon: FileText, color: 'text-orange-600', bg: 'bg-orange-500/10', label: f.toUpperCase() };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(f)) return { icon: ImageIcon, color: 'text-violet-600', bg: 'bg-violet-500/10', label: 'Image' };
  return { icon: File, color: 'text-slate-600', bg: 'bg-slate-500/10', label: f ? f.toUpperCase() : 'Fichier' };
};

// URL forçant le téléchargement (Cloudinary → fl_attachment ; sinon l'URL telle quelle).
const downloadUrl = (d) =>
  d.url && /res\.cloudinary\.com/.test(d.url) ? d.url.replace('/upload/', '/upload/fl_attachment/') : d.url;

const MAX_MB = 10; // limite raisonnable d'upload (au-delà : conseiller un lien externe)

export default function Documents() {
  const { isAdmin, user, userData } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState('file'); // 'file' | 'link'
  const [file, setFile] = useState(null);
  const [newDocument, setNewDocument] = useState({ title: '', description: '', url: '' });
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const docsQuery = query(collection(db, 'documents'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(docsQuery);
      setDocuments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewDocument({ title: '', description: '', url: '' });
    setFile(null);
    setMode('file');
    setUploadPct(0);
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop lourd (max ${MAX_MB} Mo). Utilisez plutôt un lien externe.`);
      return;
    }
    setFile(f);
    // Pré-remplit le titre avec le nom du fichier (sans extension) si vide.
    if (!newDocument.title.trim()) {
      setNewDocument((prev) => ({ ...prev, title: f.name.replace(/\.[^.]+$/, '') }));
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    if (!newDocument.title.trim()) { toast.error('Donnez un titre au document.'); return; }

    setCreating(true);
    try {
      let payload = {
        title: newDocument.title.trim(),
        description: newDocument.description.trim(),
        url: '',
        fileName: '',
        format: '',
        bytes: 0,
        source: 'link',
        createdAt: serverTimestamp(),
        createdBy: user?.uid || null,
        createdByName: userData?.fullName || 'Admin',
        downloads: 0
      };

      if (mode === 'file') {
        if (!file) { toast.error('Choisissez un fichier à téléverser.'); setCreating(false); return; }
        if (!isCloudinaryConfigured()) { toast.error('Téléversement indisponible (Cloudinary non configuré).'); setCreating(false); return; }
        setUploadPct(1);
        const res = await uploadToCloudinary(file, { folder: 'documents', onProgress: setUploadPct });
        payload = {
          ...payload,
          url: res.secureUrl,
          fileName: file.name,
          format: res.format || extOf(file.name),
          bytes: res.bytes || file.size,
          source: 'cloudinary',
          publicId: res.publicId || null,
          resourceType: res.resourceType || null
        };
      } else {
        const link = newDocument.url.trim();
        if (!link) { toast.error('Collez le lien du document.'); setCreating(false); return; }
        payload = { ...payload, url: link, format: extOf(link) };
      }

      await addDoc(collection(db, 'documents'), payload);
      toast.success('Document ajouté avec succès');
      setDialogOpen(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error(error?.message || 'Erreur lors de l\'ajout');
    } finally {
      setCreating(false);
      setUploadPct(0);
    }
  };

  const handleDeleteDocument = async () => {
    if (!docToDelete) return;
    setDeletingDoc(true);
    try {
      await deleteDoc(doc(db, 'documents', docToDelete.id));
      toast.success('Document supprimé avec succès');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingDoc(false);
      setDocToDelete(null);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <Shield className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
        <p className="text-muted-foreground">Cette page est réservée aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Documents</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">Gérez les documents partagés du Dahira</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { if (!creating) { setDialogOpen(o); if (!o) resetForm(); } }}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 w-full sm:w-auto" data-testid="add-document-btn">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un document
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Ajouter un document</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateDocument} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre</Label>
                <Input
                  id="title"
                  value={newDocument.title}
                  onChange={(e) => setNewDocument({ ...newDocument, title: e.target.value })}
                  placeholder="Nom du document"
                  required
                  data-testid="document-title-input"
                />
              </div>

              {/* Choix de la source : fichier (Cloudinary) ou lien externe */}
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode('file')} className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${mode === 'file' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  <UploadCloud className="h-4 w-4" /> Téléverser
                </button>
                <button type="button" onClick={() => setMode('link')} className={`flex items-center justify-center gap-2 rounded-lg border-2 px-3 py-2 text-sm font-medium transition-all ${mode === 'link' ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-primary/40'}`}>
                  <Link2 className="h-4 w-4" /> Lien externe
                </button>
              </div>

              {mode === 'file' ? (
                <div className="space-y-2">
                  {file ? (
                    <div className="flex items-center gap-3 rounded-lg border p-3">
                      {(() => { const M = fileMeta(extOf(file.name)); const Icon = M.icon; return <span className={`rounded-lg p-2 ${M.bg}`}><Icon className={`h-5 w-5 ${M.color}`} /></span>; })()}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{fmtSize(file.size)}</p>
                      </div>
                      {!creating && (
                        <button type="button" onClick={() => setFile(null)} className="text-muted-foreground hover:text-destructive" aria-label="Retirer">
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border py-6 text-center transition-colors hover:border-primary/50 hover:bg-muted/40" data-testid="document-file-input">
                      <UploadCloud className="h-7 w-7 text-muted-foreground" />
                      <span className="text-sm font-medium">Choisir un fichier</span>
                      <span className="text-xs text-muted-foreground">PDF, Word, Excel, image… — max {MAX_MB} Mo</span>
                      <input type="file" className="hidden" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,image/*" />
                    </label>
                  )}
                  {creating && uploadPct > 0 && (
                    <div className="space-y-1">
                      <div className="h-2 overflow-hidden rounded-full bg-muted">
                        <div className="h-full bg-primary transition-all" style={{ width: `${uploadPct}%` }} />
                      </div>
                      <p className="text-right text-xs text-muted-foreground">{uploadPct}%</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="url">URL du document</Label>
                  <Input
                    id="url"
                    value={newDocument.url}
                    onChange={(e) => setNewDocument({ ...newDocument, url: e.target.value })}
                    placeholder="https://..."
                    data-testid="document-url-input"
                  />
                  <p className="text-xs text-muted-foreground">Lien Google Drive, Dropbox, ou tout autre hébergeur (idéal pour les fichiers &gt; {MAX_MB} Mo).</p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newDocument.description}
                  onChange={(e) => setNewDocument({ ...newDocument, description: e.target.value })}
                  placeholder="Description du document (optionnel)"
                  rows={3}
                  data-testid="document-description-input"
                />
              </div>

              <Button type="submit" className="w-full" disabled={creating} data-testid="submit-document-btn">
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{mode === 'file' && uploadPct > 0 ? `Envoi ${uploadPct}%` : 'Ajout...'}</>
                ) : (
                  'Ajouter'
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Documents Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : documents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((document) => {
            const M = fileMeta(document.format || extOf(document.fileName || document.url));
            const Icon = M.icon;
            return (
              <Card key={document.id} className="card-hover border-border/50" data-testid={`document-card-${document.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${M.bg}`}>
                      <Icon className={`h-6 w-6 ${M.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate" title={document.title}>{document.title}</h3>
                      {document.description && (
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{document.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{M.label}</Badge>
                        {document.bytes ? <span className="text-xs text-muted-foreground">{fmtSize(document.bytes)}</span> : null}
                        {document.source === 'link' && <span className="text-xs text-muted-foreground">· lien externe</span>}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {document.createdByName ? `${document.createdByName} · ` : ''}
                        {document.createdAt?.toDate?.() ? document.createdAt.toDate().toLocaleDateString('fr-FR') : ''}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <a href={document.url} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full" data-testid={`open-document-${document.id}`}>
                        <ExternalLink className="h-4 w-4 mr-2" /> Ouvrir
                      </Button>
                    </a>
                    <a href={downloadUrl(document)} target="_blank" rel="noopener noreferrer" download>
                      <Button variant="ghost" size="icon" title="Télécharger" data-testid={`download-document-${document.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => setDocToDelete(document)}
                      data-testid={`delete-document-${document.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-lg font-medium text-foreground mb-2">Aucun document</h3>
            <p className="text-muted-foreground">Téléversez votre premier document (PDF, Word, Excel, image…).</p>
          </CardContent>
        </Card>
      )}

      {/* Delete Document Confirmation Modal */}
      <ConfirmDeleteDialog
        open={!!docToDelete}
        onOpenChange={(open) => !open && setDocToDelete(null)}
        onConfirm={handleDeleteDocument}
        title="Supprimer ce document ?"
        description="Cette action est irréversible. Le document sera définitivement supprimé."
        itemName={docToDelete?.title}
        confirmText="Supprimer le document"
        loading={deletingDoc}
        variant="danger"
      />
    </div>
  );
}
